import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-55">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-56">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-57" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden z-10" data-id="element-58">
            <div className="flex items-center justify-between px-8 py-5 border-b border-slate-100" data-id="element-59">
              <h3 className="text-xl font-bold text-slate-900 font-heading" data-id="element-60">
                {title}
              </h3>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600" aria-label="Cerrar" data-id="element-61">
                <X size={20} data-id="element-62" />
              </button>
            </div>
            <div className="px-8 py-6 overflow-y-auto max-h-[calc(80vh-80px)] text-slate-600 text-sm leading-relaxed space-y-4" data-id="element-63">
              {children}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-slate-950 py-12 text-center text-slate-500 text-sm" data-id="element-64">
        <div className="max-w-7xl mx-auto px-6" data-id="element-65">
          <p data-id="element-66">
            © {new Date().getFullYear()} Reinoso Business Consultant LLC. Todos
            los derechos reservados.
          </p>
          <p className="mt-3" data-id="element-67">
            <a href="mailto:jesus@reinosobusinessconsultant.com" className="text-slate-400 hover:text-brand-amber transition-colors" data-id="element-68">
              jesus@reinosobusinessconsultant.com
            </a>
          </p>
          <div className="flex items-center justify-center gap-4 mt-4" data-id="element-69">
            <button onClick={() => setActiveModal('privacy')} className="text-slate-500 hover:text-brand-amber transition-colors underline underline-offset-2" data-id="element-70">
              Política de Privacidad
            </button>
            <span className="text-slate-700" data-id="element-71">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-slate-500 hover:text-brand-amber transition-colors underline underline-offset-2" data-id="element-72">
              Términos y Condiciones
            </button>
          </div>
          <p className="mt-4 text-xs text-slate-600 max-w-2xl mx-auto" data-id="element-73">
            La información proporcionada en este sitio web no constituye
            asesoramiento legal. Nuestros servicios se basan en la Ley Federal
            de Informes Justos de Crédito (FCRA).
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-74">
        <p data-id="element-75">
          <strong data-id="element-76">Última actualización:</strong> Marzo 2026
        </p>
        <p data-id="element-77">
          En Reinoso Business Consultant LLC, nos comprometemos a proteger la
          privacidad y seguridad de su información personal. Esta política
          describe cómo recopilamos, usamos y protegemos sus datos.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-78">
          1. Información que Recopilamos
        </h4>
        <p data-id="element-79">
          Podemos recopilar información personal como su nombre, dirección de
          correo electrónico, número de teléfono y detalles financieros
          relevantes cuando usted utiliza nuestros servicios o se comunica con
          nosotros.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-80">
          2. Uso de la Información
        </h4>
        <p data-id="element-81">
          Utilizamos su información exclusivamente para proporcionar nuestros
          servicios de reparación de crédito, comunicarnos con usted sobre su
          caso y mejorar nuestros servicios. No vendemos ni compartimos su
          información personal con terceros para fines de marketing.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-82">
          3. Protección de Datos
        </h4>
        <p data-id="element-83">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger su información personal contra acceso no autorizado,
          alteración, divulgación o destrucción.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-84">4. Sus Derechos</h4>
        <p data-id="element-85">
          Usted tiene derecho a acceder, corregir o eliminar su información
          personal en cualquier momento. Para ejercer estos derechos,
          contáctenos a través de nuestros canales oficiales.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-86">5. Contacto</h4>
        <p data-id="element-87">
          Si tiene preguntas sobre esta política de privacidad, puede
          contactarnos a través de nuestro sitio web o los canales de
          comunicación proporcionados.
        </p>
      </Modal>

      {/* Terms & Conditions Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-88">
        <p data-id="element-89">
          <strong data-id="element-90">Última actualización:</strong> Marzo 2026
        </p>
        <p data-id="element-91">
          Al utilizar los servicios de Reinoso Business Consultant LLC, usted
          acepta los siguientes términos y condiciones. Por favor, léalos
          cuidadosamente.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-92">1. Servicios</h4>
        <p data-id="element-93">
          Reinoso Business Consultant LLC ofrece servicios de reparación y
          restauración de crédito basados en los derechos otorgados por la Ley
          Federal de Informes Justos de Crédito (FCRA). Nuestros servicios
          incluyen la revisión de reportes de crédito, identificación de errores
          y disputas ante los burós de crédito.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-94">
          2. No Garantía de Resultados
        </h4>
        <p data-id="element-95">
          Si bien nos esforzamos por obtener los mejores resultados posibles, no
          podemos garantizar resultados específicos. Cada caso es único y los
          resultados pueden variar según las circunstancias individuales.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-96">
          3. Responsabilidades del Cliente
        </h4>
        <p data-id="element-97">
          El cliente se compromete a proporcionar información veraz y completa,
          y a cooperar con nuestro equipo durante todo el proceso de reparación
          de crédito.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-98">4. Cancelación</h4>
        <p data-id="element-99">
          Usted puede cancelar nuestros servicios en cualquier momento. Los
          términos específicos de cancelación y reembolso se detallarán en su
          acuerdo de servicio individual.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-100">
          5. Limitación de Responsabilidad
        </h4>
        <p data-id="element-101">
          Reinoso Business Consultant LLC no será responsable por daños
          indirectos, incidentales o consecuentes que surjan del uso de nuestros
          servicios. Nuestra responsabilidad máxima se limitará al monto pagado
          por los servicios.
        </p>
        <h4 className="font-semibold text-slate-900 pt-2" data-id="element-102">6. Ley Aplicable</h4>
        <p data-id="element-103">
          Estos términos se rigen por las leyes federales de los Estados Unidos
          y las leyes del estado donde opera Reinoso Business Consultant LLC.
        </p>
      </Modal>
    </>;
}